import { ExclamationCircleFilled, FilePdfOutlined } from "@ant-design/icons";
import {
    Button,
    ConfigProvider,
    DatePicker,
    Form,
    Input,
    Modal,
    Select,
    Spin,
    Switch,
    Table,
    Tag,
    Tooltip,
} from "antd";
import frFR from "antd/locale/fr_FR";
import { createRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import { rightsService } from "../../services/rightsService";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import { frenchDate } from "../../variables/functions";
import RecuCurrentFormat from "../production/components/docs/RecuCurrentFormat";
import { displayNumbers } from "../../variables/numbersFunctions";
import dayjs from "dayjs";

const { confirm } = Modal;
const { Option } = Select;
const { RangePicker } = DatePicker;

/* ── Couleurs de tag par mode de paiement ─────────────────────────── */
const modeTag = (mode) => {
    switch (mode) {
        case "ESPECES": return <Tag color="blue" style={tagStyle}>Espèces</Tag>;
        case "BANQUE": return <Tag color="purple" style={tagStyle}>Chèque</Tag>;
        case "VIREMENT": return <Tag color="cyan" style={tagStyle}>Virement</Tag>;
        case "MOBILE_MONEY": return <Tag color="orange" style={tagStyle}>Mobile Money</Tag>;
        case "RISTOURNE": return <Tag color="green" style={tagStyle}>Ristourne</Tag>;
        case "COMPENSATION": return <Tag color="geekblue" style={tagStyle}>Compensation</Tag>;
        default: return null;
    }
};

const tagStyle = { fontWeight: 600, letterSpacing: "0.03em", padding: "2px 10px" };

/* ── Carte statistique ────────────────────────────────────────────── */
const StatCard = ({ label, sublabel, count, amount, icon, bg }) => (
    <div className="col-sm-6 col-xl-3">
        <div
            className="card border-0 h-100"
            style={{
                borderRadius: 14,
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    height: 4,
                    background: bg,
                    borderRadius: "14px 14px 0 0",
                }}
            />
            <div className="card-body" style={{ padding: "1.2rem 1.4rem" }}>
                <div className="d-flex align-items-start justify-content-between">
                    <div>
                        <p
                            className="text-muted mb-1"
                            style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}
                        >
                            {label}
                        </p>
                        <h4 className="mb-0 fw-bold" style={{ color: "#1a1a2e", lineHeight: 1.2 }}>
                            {count ?? 0}
                        </h4>
                        <p className="mb-0 mt-1" style={{ fontSize: 13 }}>
                            <span style={{ fontWeight: 600, color: "#333" }}>
                                {displayNumbers(Math.trunc(amount ?? 0))}
                            </span>{" "}
                            <span className="text-muted">FCFA</span>
                        </p>
                    </div>
                    <div
                        style={{
                            width: 46,
                            height: 46,
                            borderRadius: "50%",
                            background: bg + "22",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <i className={icon} style={{ fontSize: 22, background: bg, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }} />
                    </div>
                </div>
                <small className="text-muted" style={{ fontSize: 11 }}>{sublabel}</small>
            </div>
        </div>
    </div>
);

/* ═══════════════════════════════════════════════════════════════════ */

const Encaissements = () => {
    const [datas, setDatas] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loadingData, setLoadingData] = useState(true);
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [modalModePaiement, setModalModePaiement] = useState(false);
    const [contractToPay, setContractToPay] = useState("");
    const [numeroPaiement, setNumeroPaiement] = useState("P");
    const [modePaiement, setModePaiement] = useState("");
    const [modePaiementForm] = Form.useForm();
    const [banques, setBanques] = useState([]);
    const [banque, setBanque] = useState("BANQUE");
    const [dateF, setDateF] = useState([]);
    const [reglement, setReglement] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [useCompensationRistourne, setUseCompensationRistourne] = useState(false);
    const [havingRistourne, setHavingRistourne] = useState(false);
    const [count, setCount] = useState("");
    const [dateVirement, setDateVirement] = useState(null);
    const [reference, setReference] = useState("");

    const navigate = useNavigate();

    const handleChange = (pagination, filters, sorter) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
        setFilteredInfo(filters);
        setSortedInfo(sorter);
    };

    useEffect(() => {
        axiosInstance
            .get(`/api/reglement`, {
                params: {
                    page: currentPage - 1,
                    size: pageSize,
                    search: "",
                    startDate: dateF ? dateF[0] : null,
                    endDate: dateF ? dateF.length > 1 ? dateF[1] : null : null,
                    compagnie: "",
                },
            })
            .then((res) => {
                //  console.log("Encaissements: ", res.data);
                setDatas(res.data.content);
                setTotal(res.data.totalElements);
                setLoadingData(false);
            })
            .catch((err) => {
                console.log("Err: ", err);
                setLoadingData(false);
            });

        axiosInstance
            .get(`/api/reglement/encaissements-count`)
            .then((res) => setCount(res.data))
            .catch(console.log);

        axiosInstance
            .get(`/api/parametre/type/BANQUE`)
            .then((res) => setBanques(res.data))
            .catch(console.log);
    }, [currentPage, pageSize, dateF]);

    const encaisserContrat = (id) => {
        axiosInstance
            .get(`/api/reglement/check-ristourne/${id.codeId}`)
            .then((resp) => setHavingRistourne(resp.data))
            .catch(console.log);
        setModalModePaiement(true);
        setContractToPay(id);
    };

    /* ── Colonnes du tableau ──────────────────────────────────────── */
    const columns = [
        {
            title: "Assuré",
            key: "assure",
            render: (_, record) => (
                <div>
                    <span style={{ fontWeight: 600, color: "#1a1a2e" }}>
                        {record.nomSouscripteur || ""}
                    </span>
                    {record.prenomSouscripteur && (
                        <span style={{ color: "#555" }}> {record.prenomSouscripteur}</span>
                    )}
                </div>
            ),
        },
        {
            title: "N° Police",
            key: "numeroPolice",
            render: (_, record) => (
                <span
                    style={{
                        fontFamily: "monospace",
                        fontWeight: 600,
                        color: (record.numeroPolice || record.police) ? "#1677ff" : "#aaa",
                        fontSize: 13,
                    }}
                >
                    {record.numeroPolice || record.police || "—"}
                </span>
            ),
        },
        {
            title: "Avenant",
            key: "avenant",
            render: (_, record) => (
                <Tag
                    style={{
                        fontWeight: 700,
                        fontSize: 12,
                        background: "#f0f5ff",
                        color: "#1677ff",
                        border: "1px solid #91caff",
                        borderRadius: 6,
                    }}
                >
                    {record?.avenant || "AN"}
                </Tag>
            ),
        },
        {
            title: "Montant",
            key: "montant",
            align: "right",
            render: (_, record) => (
                <span style={{ fontWeight: 700, color: "#1a1a2e", fontSize: 14 }}>
                    {displayNumbers(record.montant)}{" "}
                    <small style={{ fontWeight: 400, color: "#888", fontSize: 11 }}>FCFA</small>
                </span>
            ),
        },
        {
            title: "Statut",
            key: "statut",
            render: (_, record) => {
                const encaisse = record.modePaiement && record.modePaiement !== "-" && record?.dateReglement;
                return encaisse ? (
                    <Tag
                        color="success"
                        style={{ fontWeight: 700, borderRadius: 20, padding: "2px 12px" }}
                    >
                        Encaissé
                    </Tag>
                ) : (
                    <Tag
                        color="warning"
                        style={{ fontWeight: 700, borderRadius: 20, padding: "2px 12px" }}
                    >
                        En attente
                    </Tag>
                );
            },
            filters: [
                { text: "Encaissé", value: "encaisse" },
                { text: "En attente", value: "attente" },
            ],
        },
        {
            title: "Mode paiement",
            key: "modePaiement",
            render: (_, record) => {
                const encaisse = record.modePaiement && record.modePaiement !== "-" && record?.dateReglement;
                return encaisse ? modeTag(record.modePaiement) : null;
            },
            filters: [
                { text: "Espèces", value: "ESPECE" },
                { text: "Ristourne", value: "RISTOURNE" },
                { text: "Chèque", value: "BANQUE" },
                { text: "Mobile Money", value: "MOBILE_MONEY" },
                { text: "Compensation", value: "COMPENSATION" },
                { text: "Non payé", value: "-" },
            ],
            filteredValue: filteredInfo.modePaiement || null,
            onFilter: (value, record) => record.modePaiement === value,
        },
        {
            title: "Date opération",
            key: "createdAt",
            render: (_, record) => (
                <span style={{ color: "#555", fontSize: 13 }}>{frenchDate(record.createdAt)}</span>
            ),
        },
        {
            title: "Date règlement",
            key: "dateReglement",
            render: (_, record) => (
                <span style={{ color: "#555", fontSize: 13 }}>
                    {record.modePaiement && record?.dateReglement ? frenchDate(record.dateReglement) : "—"}
                </span>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, record) => (
                <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                    {rightsService("HLA037") &&
                        record.numeroPolice &&
                        (!record.modePaiement || !record?.dateReglement) && (
                            <Tooltip title="Encaisser">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-success btn-icon rounded-pill"
                                    onClick={() => encaisserContrat(record)}
                                >
                                    <i className="bx bxs-credit-card" />
                                </button>
                            </Tooltip>
                        )}
                    <Tooltip title="Voir le contrat">
                        <button
                            type="button"
                            className="btn btn-sm btn-primary btn-icon rounded-pill"
                            onClick={() => navigate(`/details-contrat/${record.contratCodeId}`)}
                        >
                            <i className="bx bx-dots-vertical-rounded" />
                        </button>
                    </Tooltip>
                    {rightsService("HCO006") &&
                        record.numeroPolice &&
                        record.modePaiement && record?.dateReglement && (
                            <Tooltip title="Annuler l'encaissement">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger btn-icon rounded-pill"
                                    onClick={() => alertCancelReglement(record)}
                                >
                                    <i className="bx bx-x" />
                                </button>
                            </Tooltip>
                        )}
                    {rightsService("HCO006") &&
                        record.numeroPolice &&
                        record.modePaiement && record?.dateReglement && (
                            <Tooltip title="Imprimer le reçu">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-success btn-icon rounded-pill"
                                    onClick={() => {
                                        setReglement(record);
                                        setModalOpen(true);
                                    }}
                                >
                                    <i className="bx bxs-file-pdf" />
                                </button>
                            </Tooltip>
                        )}
                </div>
            ),
        },
    ];

    /* ── Confirmations ────────────────────────────────────────────── */
    const alertReglement = (contractToPay) => {
        confirm({
            title: "Confirmer l'encaissement",
            icon: <ExclamationCircleFilled />,
            content: "Toutes vos actions sont historisées.",
            okText: "Oui, encaisser",
            okType: "primary",
            cancelText: "Annuler",
            onOk() { reglementContrat(contractToPay); },
        });
    };

    const alertCancelReglement = (reglementToCancel) => {
        confirm({
            title: "Annuler cet encaissement ?",
            icon: <ExclamationCircleFilled />,
            content: "Toutes vos actions sont historisées.",
            okText: "Oui, annuler",
            okType: "danger",
            cancelText: "Retour",
            onOk() { cancelReglement(reglementToCancel); },
        });
    };

    const cancelReglement = (data) => {
        axiosInstance
            .get(`/api/reglement/cancel/${data.contratCodeId}/${data.codeId}`)
            .then(() => {
                window.location.reload();
                toast("Règlement annulé avec succès");
            })
            .catch(() => toast("Erreur lors de l'annulation du règlement"));
    };

    const reglementContrat = (data) => {
        if (useCompensationRistourne) {
            const url = modePaiement === "ESPECES"
                ? `/api/reglement/compensation/${data.contratCodeId}/${data.codeId}`
                : `/api/reglement/compensation/${data.contratCodeId}/${data.codeId}/${modePaiement}/${numeroPaiement}/${banque}`;
            axiosInstance.get(url)
                .then(() => { window.location.reload(); toast("Règlement effectué avec succès"); })
                .catch(() => toast("Erreur lors du règlement"));
        } else {
            axiosInstance
                .get(
                    `/api/reglement/${data.contratCodeId}/${data.codeId}/${LOGGED_USER.codeId}/${modePaiement}/${numeroPaiement}/${banque ? banque : reference}/${data.montant}/${data.avenant}`,
                )
                .then(() => { window.location.reload(); toast("Règlement effectué avec succès"); })
                .catch(() => toast("Erreur lors du règlement"));
        }
    };

    const dateFiltering = (_, dateString) => {
        const [startDate, endDate] = dateString;
        setDateF(dateString);
        setFilteredInfo({ ...filteredInfo, startDate, endDate });
    };

    const printRecu = createRef();
    const handleRecu = useReactToPrint({ content: () => printRecu.current });

    /* ── Rendu ────────────────────────────────────────────────────── */
    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"CAISSE"} />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* ── En-tête de page ───────────────────────────── */}
                            <div className="mb-5">
                                <h4
                                    className="mb-1 fw-bold"
                                    style={{ color: "#1a1a2e", fontSize: 22 }}
                                >
                                    <i
                                        className="bx bx-wallet me-2"
                                        style={{ color: "#1677ff", verticalAlign: "middle" }}
                                    />
                                    Encaissements
                                </h4>
                                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                                    Suivi des règlements et encaissements de primes
                                </p>
                            </div>

                            {/* ── Cartes statistiques ───────────────────────── */}
                            <div className="row g-4 mb-5">
                                <StatCard
                                    label="Aujourd'hui"
                                    sublabel="Encaissements du jour"
                                    count={count?.numberOfReglementsToday}
                                    amount={count?.totalAmountToday}
                                    icon="bx bx-calendar-check bx-sm"
                                    bg="linear-gradient(135deg,#1677ff,#4299e1)"
                                />
                                <StatCard
                                    label="Ce mois"
                                    sublabel="Encaissements du mois"
                                    count={count?.numberOfReglementsThisMonth}
                                    amount={count?.totalAmountThisMonth}
                                    icon="bx bx-line-chart bx-sm"
                                    bg="linear-gradient(135deg,#7c3aed,#a78bfa)"
                                />
                                <StatCard
                                    label="Total"
                                    sublabel="Tous les encaissements"
                                    count={total}
                                    amount={count?.totalAmount}
                                    icon="bx bx-money bx-sm"
                                    bg="linear-gradient(135deg,#059669,#34d399)"
                                />
                                <StatCard
                                    label="Non encaissés"
                                    sublabel="En attente de règlement"
                                    count={count?.numberOfNonCashedReglements}
                                    amount={count?.totalAmountNonCashed}
                                    icon="bx bx-time-five bx-sm"
                                    bg="linear-gradient(135deg,#dc2626,#f87171)"
                                />
                            </div>

                            {/* ── Section tableau ───────────────────────────── */}
                            <div
                                className="card border-0"
                                style={{
                                    borderRadius: 14,
                                    boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                                    overflow: "hidden",
                                }}
                            >
                                {/* Barre filtres */}
                                <div
                                    className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-3"
                                    style={{
                                        background: "#fafbff",
                                        borderBottom: "1px solid #e8edf5",
                                        padding: "1rem 1.4rem",
                                    }}
                                >
                                    <div>
                                        <h6 className="mb-0 fw-bold" style={{ color: "#1a1a2e" }}>
                                            Liste des encaissements
                                        </h6>
                                        <small className="text-muted">
                                            {total} enregistrement{total > 1 ? "s" : ""}
                                        </small>
                                    </div>
                                    <ConfigProvider locale={frFR}>
                                        <RangePicker
                                            onChange={dateFiltering}
                                            placeholder={["Date début", "Date fin"]}
                                            disabledDate={(current) =>
                                                current && current > dayjs().endOf("day")
                                            }
                                            style={{ borderRadius: 8 }}
                                        />
                                    </ConfigProvider>
                                </div>

                                {/* Tableau */}
                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingData}>
                                        <Table
                                            rowKey={(record) =>
                                                record.codeId || record.contratCodeId || Math.random()
                                            }
                                            columns={columns}
                                            dataSource={datas}
                                            onChange={handleChange}
                                            size="middle"
                                            pagination={{
                                                current: currentPage,
                                                pageSize: pageSize,
                                                total: total,
                                                showSizeChanger: true,
                                                showTotal: (t) =>
                                                    `${t} encaissement${t > 1 ? "s" : ""}`,
                                            }}
                                            rowClassName={(_, index) =>
                                                index % 2 === 0 ? "table-row-even" : "table-row-odd"
                                            }
                                            style={{ borderRadius: 0 }}
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </div>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade" />
                    </div>
                </div>
            </div>

            {/* ── Modal reçu ────────────────────────────────────────── */}
            {reglement && (
                <Modal
                    title={
                        <span style={{ fontWeight: 700 }}>
                            Reçu d'encaissement{" "}
                            {reglement?.avenant?.nom ? `— ${reglement.avenant.nom}` : ""}
                        </span>
                    }
                    centered
                    open={modalOpen}
                    onOk={() => setModalOpen(false)}
                    onCancel={() => setModalOpen(false)}
                    width={1000}
                    zIndex={2000}
                    footer={[
                        <Button
                            key="print"
                            type="primary"
                            icon={<FilePdfOutlined />}
                            disabled={!rightsService("HCO008")}
                            onClick={handleRecu}
                            style={{ background: "#059669", borderColor: "#059669" }}
                        >
                            Imprimer le reçu
                        </Button>,
                        <Button key="close" onClick={() => setModalOpen(false)}>
                            Fermer
                        </Button>,
                    ]}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            height: "135vh",
                            margin: 0,
                            padding: 0,
                        }}
                    >
                        <RecuCurrentFormat
                            ref={printRecu}
                            contrat={reglement.contrat}
                            reglement={reglement}
                        />
                    </div>
                </Modal>
            )}

            {/* ── Modal mode de paiement ────────────────────────────── */}
            <Modal
                title={
                    <span style={{ fontWeight: 700 }}>
                        <i className="bx bxs-credit-card me-2" style={{ color: "#1677ff" }} />
                        Encaisser la prime
                    </span>
                }
                centered
                open={modalModePaiement}
                okText="Confirmer"
                cancelText="Annuler"
                onOk={() => alertReglement(contractToPay)}
                onCancel={() => setModalModePaiement(false)}
            >
                <div className="container" style={{ paddingTop: 8 }}>
                    <Form form={modePaiementForm} layout="vertical">
                        {havingRistourne && (
                            <Form.Item
                                name="useCompensation"
                                label="Utiliser la compensation de ristourne ?"
                            >
                                <Switch
                                    checkedChildren="OUI"
                                    unCheckedChildren="NON"
                                    onChange={(checked) => setUseCompensationRistourne(checked)}
                                    checked={useCompensationRistourne}
                                />
                            </Form.Item>
                        )}

                        <Form.Item
                            name="modePaiement"
                            label="Mode de paiement"
                            rules={[{ required: true, message: "Champ obligatoire" }]}
                        >
                            <Select
                                onChange={(value) => setModePaiement(value)}
                                placeholder="Sélectionnez un mode de paiement"
                                style={{ borderRadius: 8 }}
                            >
                                <Option value="ESPECES">Espèces</Option>
                                <Option value="BANQUE">Chèque bancaire</Option>
                                <Option value="VIREMENT">Virement bancaire</Option>
                                <Option value="MOBILE_MONEY">Mobile Money (Flooz / TMoney)</Option>
                                {contractToPay.montant < 0 && (
                                    <Option value="RISTOURNE">Ristourne</Option>
                                )}
                            </Select>
                        </Form.Item>

                        {(modePaiement === "BANQUE" || modePaiement === "MOBILE_MONEY") && (
                            <Form.Item
                                name="numeroPaiement"
                                label={modePaiement === "BANQUE" ? "Numéro de chèque" : "Numéro Flooz / TMoney"}
                                rules={[{ required: true, message: "Champ obligatoire" }]}
                            >
                                <Input
                                    placeholder={
                                        modePaiement === "BANQUE"
                                            ? "Ex : 001234"
                                            : "Numéro de téléphone"
                                    }
                                    onChange={(e) => setNumeroPaiement(e.target.value)}
                                />
                            </Form.Item>
                        )}

                        {(modePaiement === "BANQUE" || modePaiement === "VIREMENT") && (
                            <Form.Item
                                name="banque"
                                label="Banque"
                                rules={[{ required: true, message: "Champ obligatoire" }]}
                            >
                                <Select
                                    onChange={(value) => setBanque(value)}
                                    placeholder="Sélectionnez la banque"
                                >
                                    {banques.map((b) => (
                                        <Option key={b.parametreId} value={b.libelleParametre}>
                                            {b.libelleParametre}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}

                        {(modePaiement === "MOBILE_MONEY" || modePaiement === "VIREMENT") && (
                            <Form.Item
                                name="referencePaiement"
                                label="Référence du paiement"
                                rules={[{ required: true, message: "Champ obligatoire" }]}
                            >
                                <Input
                                    placeholder="Numéro de référence"
                                    onChange={(e) => setReference(e.target.value)}
                                />
                            </Form.Item>
                        )}

                        {modePaiement === "VIREMENT" && (
                            <Form.Item
                                name="dateVirement"
                                label="Date du virement"
                                rules={[{ required: true, message: "Champ obligatoire" }]}
                            >
                                <DatePicker
                                    placeholder="Date du virement"
                                    onChange={(_, value) => setDateVirement(value)}
                                    style={{ width: "100%" }}
                                    disabledDate={(current) =>
                                        current && current > dayjs().endOf("day")
                                    }
                                />
                            </Form.Item>
                        )}
                    </Form>
                </div>
            </Modal>

            <div className="layout-overlay layout-menu-toggle" />
            <div className="drag-target" />
        </div>
    );
};

export default Encaissements;
